import { Alert, Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';

/**
 * How much this customer orders, and how long they wait.
 *
 * Two years of months rather than a total: a customer who stopped ordering
 * last spring and one who orders every week look identical as a count, and the
 * difference is the thing worth seeing on their page.
 */
export function OrderTrend({ monthly, averageDeliveryDays, measuredOn }: {
  monthly: readonly { month: string; orders: number }[];
  averageDeliveryDays: number | null;
  measuredOn: number;
}) {
  if (monthly.length === 0) return null;

  const max = Math.max(1, ...monthly.map(point => point.orders));
  const anyInWindow = monthly.some(point => point.orders > 0);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="overline" color="text.secondary">Objednávky za 24 mesiacov</Typography>
          {averageDeliveryDays == null ? (
            <Typography variant="caption" color="text.secondary">
              Priemerný čas vybavenia zatiaľ nie je z čoho spočítať
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Priemerný čas vybavenia: <strong>{averageDeliveryDays} dňa</strong> ({measuredOn} objednávok)
            </Typography>
          )}
        </Stack>

        {/* A customer whose orders are all older than the window would
            otherwise get a flat row of slivers and no explanation. */}
        {!anyInWindow && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            Za posledné dva roky žiadna objednávka — všetky sú staršie.
          </Alert>
        )}

        <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 110, mt: 2 }}>
          {monthly.map(point => (
            <Tooltip key={point.month} title={`${point.month}: ${point.orders}`} arrow>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 4,
                  // Empty months keep a sliver, so a gap in trade reads as a
                  // gap rather than as the chart simply ending.
                  height: `${Math.max((point.orders / max) * 100, 2)}%`,
                  bgcolor: point.orders === 0 ? 'action.disabledBackground' : 'primary.main',
                  borderRadius: 0.5
                }}
              />
            </Tooltip>
          ))}
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">{monthly[0]?.month}</Typography>
          <Typography variant="caption" color="text.secondary">najviac za mesiac: {max}</Typography>
          <Typography variant="caption" color="text.secondary">{monthly[monthly.length - 1]?.month}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
